/**
 * A browser-readable copy of the checkout id, kept purely so the server can see
 * it (BEC-25061). The authoritative store is still localforage / IndexedDB,
 * which never reaches the server.
 *
 * Written client-side via document.cookie, so it CANNOT be httpOnly. That is
 * not a downgrade on its own — the id is already readable by any script on the
 * origin via IndexedDB — but it does mean the value now travels on same-origin
 * requests and may appear in server logs. Session-scoped (no Max-Age) to limit
 * how long it lives.
 *
 * Nothing depends on this cookie being present: if it is missing, stale, or
 * disagrees with localforage, callers must fall back to the existing client
 * restore path.
 *
 * SameSite=Strict deliberately MATCHES the auth cookies (see
 * lib/auth/cookieStorage buildCookieAttributes). With Lax, a cross-site
 * top-level navigation - an email link, an external referrer - would send this
 * cookie while the browser withheld the Strict auth cookies, so the server
 * would see a checkout id and no session and take a signed-in customer down
 * the guest prefetch path. Strict keeps the two in lockstep: either both are
 * visible or neither is. Browsers still send Strict cookies for typed URLs,
 * bookmarks and reloads, which are the navigations the prefetch exists for.
 */
export const CHECKOUT_ID_COOKIE = 'bre_checkout_id';

const isBrowser = () =>
  globalThis.window !== undefined && typeof document !== 'undefined';

/** Expire the cookie. Client-only; a no-op on the server. */
export const clearCheckoutIdCookie = (): void => {
  if (!isBrowser()) return;
  document.cookie = `${CHECKOUT_ID_COOKIE}=; path=/; Max-Age=0; SameSite=Strict`;
};

/**
 * Mirror the checkout id into a session cookie. Writing an empty value clears
 * it instead, so callers never have to special-case a missing id.
 */
export const writeCheckoutIdCookie = (
  checkoutId: string | null | undefined,
): void => {
  if (!isBrowser()) return;

  if (!checkoutId) {
    clearCheckoutIdCookie();
    return;
  }

  const secure = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${CHECKOUT_ID_COOKIE}=${encodeURIComponent(
    checkoutId,
  )}; path=/; SameSite=Strict${secure}`;
};

/**
 * Read the id from a raw Cookie header. Works server-side from
 * `req.headers.cookie` and client-side from `document.cookie`.
 */
export const readCheckoutIdFromCookieHeader = (
  cookieHeader?: string | null,
): string | null => {
  if (!cookieHeader) return null;

  for (const part of cookieHeader.split(';')) {
    const index = part.indexOf('=');
    if (index < 0) continue;
    if (part.slice(0, index).trim() !== CHECKOUT_ID_COOKIE) continue;

    const raw = part.slice(index + 1).trim();
    if (!raw) return null;
    try {
      return decodeURIComponent(raw);
    } catch {
      // Malformed percent-escape - treat as absent rather than throwing during
      // SSR, so the page falls back to the client restore path.
      return null;
    }
  }

  return null;
};
